import { useState, useRef, useEffect, useMemo } from 'react';
import { GLOSSARY_BY_SLUG, GLOSSARY_MATCHES, getGlossaryAudioUrl } from '../lib/glossaryTerms';

/**
 * GlossaryText
 *
 * Envuelve un texto y detecta terminos del glosario para hacerlos clickeables.
 * - Solo subraya la PRIMERA ocurrencia de cada termino (evita ruido visual).
 * - Click abre tooltip con definicion + ejemplo + audio play.
 * - En mobile abre como bottom sheet (mejor UX en pantallas pequenas).
 *
 * Uso:
 *   <GlossaryText text="El moat de Apple es brutal..." />
 *
 * Renderiza el texto como un span con palabras clickeables intercaladas.
 */
export default function GlossaryText({ text, className = '', light = false }) {
  if (!text) return null;

  const parts = useMemo(() => splitTextWithGlossary(text), [text]);

  return (
    <span className={className}>
      {parts.map((part, i) =>
        part.type === 'term' ? (
          <GlossaryTerm key={i} slug={part.slug} display={part.text} light={light} />
        ) : (
          <span key={i}>{part.text}</span>
        )
      )}
    </span>
  );
}

/**
 * Recorre el texto y devuelve un array de { type: 'text' | 'term', text, slug? }.
 * Solo la PRIMERA ocurrencia de cada slug se marca como term; las demas quedan
 * como texto plano.
 */
function splitTextWithGlossary(text) {
  const usedSlugs = new Set();
  const ranges = [];

  // Find all matches and pick the longest non-overlapping ones, first-occurrence only.
  for (const { match, slug } of GLOSSARY_MATCHES) {
    if (usedSlugs.has(slug)) continue;
    // Word-boundary regex, case-sensitive. The matches array already has variants
    // (uppercase / lowercase / plural), so we match exact strings.
    const escaped = match.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`(?<![\\w-])${escaped}(?![\\w-])`, 'g');
    let m;
    while ((m = re.exec(text)) !== null) {
      const start = m.index;
      const end = start + m[0].length;
      // Reject if overlaps with a previously claimed range
      const overlaps = ranges.some(r => start < r.end && end > r.start);
      if (overlaps) continue;
      ranges.push({ start, end, slug, text: m[0] });
      usedSlugs.add(slug);
      break; // only first occurrence
    }
  }

  // Sort by position so we can splice the text linearly
  ranges.sort((a, b) => a.start - b.start);

  const parts = [];
  let cursor = 0;
  for (const r of ranges) {
    if (r.start > cursor) {
      parts.push({ type: 'text', text: text.slice(cursor, r.start) });
    }
    parts.push({ type: 'term', text: r.text, slug: r.slug });
    cursor = r.end;
  }
  if (cursor < text.length) {
    parts.push({ type: 'text', text: text.slice(cursor) });
  }
  return parts;
}

/**
 * GlossaryTerm — palabra clickeable individual con underline punteado.
 * Click abre el tooltip (desktop) o bottom sheet (mobile).
 */
function GlossaryTerm({ slug, display, light = false }) {
  const [open, setOpen] = useState(false);
  const data = GLOSSARY_BY_SLUG[slug];
  if (!data) return <span>{display}</span>;

  const linkClass = light
    ? 'border-b border-dotted border-white/60 text-white hover:bg-white/10 rounded-sm px-0.5 cursor-pointer transition-colors'
    : 'border-b border-dotted border-[#2A59C6] text-[#2A59C6] hover:bg-[#EFF6FF] rounded-sm px-0.5 cursor-pointer transition-colors';

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={linkClass}
        type="button"
        style={{ display: 'inline' }}
      >
        {display}
      </button>
      {open && <GlossaryPopup term={data} onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * GlossaryPopup — Modal/bottom sheet con la definicion completa.
 */
function GlossaryPopup({ term, onClose }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // ESC para cerrar
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Auto-pause cuando se cierra
  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  const handlePlayToggle = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(() => {});
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-xl p-5 max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-0.5">Glosario</div>
            <div className="text-lg font-bold text-[#181D27]">{term.term}</div>
          </div>
          <button
            onClick={onClose}
            aria-label="Cerrar"
            className="w-8 h-8 rounded-full hover:bg-[#F3F4F6] flex items-center justify-center text-[#9CA3AF] text-xl"
            type="button"
          >
            ×
          </button>
        </div>

        {/* Definicion */}
        <p className="text-sm text-[#374151] leading-relaxed mb-3">{term.definition}</p>

        {/* Ejemplo */}
        <div className="bg-[#F0F4FF] rounded-lg p-3 mb-4 border border-[#DBEAFE]">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-[#2A59C6] mb-1">Ejemplo</div>
          <p className="text-xs text-[#374151] leading-relaxed">{term.example}</p>
        </div>

        {/* Audio */}
        <div className="bg-gradient-to-br from-[#0a2540] to-[#1a3a6c] rounded-lg p-3 text-white">
          <div className="flex items-center gap-3">
            <button
              onClick={handlePlayToggle}
              className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition"
              aria-label={isPlaying ? 'Pausar' : 'Reproducir'}
              type="button"
            >
              {isPlaying ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><rect x="6" y="5" width="4" height="14"/><rect x="14" y="5" width="4" height="14"/></svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>
              )}
            </button>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-wider opacity-70">Escuchar definición</div>
              <div className="text-xs opacity-90">Narrado por el Oráculo</div>
            </div>
          </div>
          <audio
            ref={audioRef}
            src={getGlossaryAudioUrl(term.slug)}
            preload="metadata"
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
            style={{ display: 'none' }}
          />
        </div>
      </div>
    </div>
  );
}
